import { Link2, Mail, Music2, PenLine, ShieldCheck, UserRound } from 'lucide-react';

const noticeDetails = [
  {
    number: '01',
    icon: UserRound,
    title: 'Claimant & Authority',
    description: 'Include your full legal name and explain whether you are the copyright owner, an artist, a record label representative, or another authorized representative. Include your record label or company name if applicable.'
  },
  {
    number: '02',
    icon: Music2,
    title: 'Copyrighted Work',
    description: 'Identify the copyrighted work, including the artist name and song or release title. Include ISRC, UPC, catalog number, or other release identifiers if available. These identifiers are helpful but optional.'
  },
  {
    number: '03',
    icon: Link2,
    title: 'PrimeTime Content',
    description: 'Provide the exact PrimeTime DJ Pool track URL or enough identifying information for our team to locate the material in question. Include the track title, version, or edit name when available.'
  },
  {
    number: '04',
    icon: Mail,
    title: 'Contact Details',
    description: 'Provide contact information sufficient for our team to reach you regarding your notice. Include your email address and relevant mailing address or telephone details. A business or label email is helpful for verification but is not mandatory.'
  },
  {
    number: '05',
    icon: ShieldCheck,
    title: 'Required Statements',
    description: 'Include a statement that you have a good-faith belief the complained-of use is not authorized by the copyright owner, its agent, or the law. Also state that the information in your notice is accurate and that, under penalty of perjury, you are authorized to act on behalf of the copyright owner.'
  },
  {
    number: '06',
    icon: PenLine,
    title: 'Signature',
    description: 'Include the physical or electronic signature of the copyright owner or the person authorized to submit the notice. A typed full name may serve as an electronic signature when appropriate.'
  }
];

export function Dmca() {
  return (
    <section className="relative isolate overflow-hidden bg-black">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute -left-24 -top-28 h-80 w-80 rounded-full bg-prime-red/20 blur-3xl" />
        <div className="absolute -right-24 -top-28 h-80 w-80 rounded-full bg-prime-red/15 blur-3xl" />
        <div className="absolute -bottom-48 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-prime-red/10 blur-3xl" />
      </div>

      <div className="container-shell py-12 sm:py-16 lg:py-20">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-sm font-black uppercase tracking-[0.35em] text-prime-red">COPYRIGHT &amp; DMCA</p>
          <h1 className="mx-auto mt-5 max-w-6xl text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            Copyright Claims &amp; Rights Verification
          </h1>
          <div className="mx-auto mt-5 h-1 w-48 -skew-x-12 rounded-full bg-prime-red shadow-glow" aria-hidden="true" />
          <p className="mx-auto mt-5 max-w-4xl text-base leading-7 text-white/65 sm:text-lg">
            Please include the following details in your email so we can review your copyright notice.
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-6xl items-stretch gap-5 md:grid-cols-3 lg:mt-12">
          {noticeDetails.map(({ number, icon: Icon, title, description }) => (
            <article key={number} className="grid h-full grid-cols-[68px_minmax(0,1fr)] gap-3 rounded-3xl border border-prime-red/60 bg-black/65 p-5 shadow-[inset_0_-28px_48px_rgba(229,9,20,0.08)] sm:p-6">
              <div className="flex flex-col items-center gap-4">
                <span className="inline-flex h-11 min-w-14 items-center justify-center rounded-xl bg-prime-red px-3 text-lg font-black tabular-nums text-white shadow-glow">
                  {number}
                </span>
                <Icon aria-hidden="true" className="text-prime-red" size={34} strokeWidth={2.1} />
              </div>
              <div className="border-l border-prime-red/45 pl-3">
                <h2 className="text-xl font-black leading-tight text-white">{title}</h2>
                <p className="mt-3 text-sm leading-5 text-white/65">{description}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mx-auto mt-7 max-w-4xl text-center text-sm italic leading-6 text-white/65 sm:text-base">
          <p>Independent artists, copyright owners, record labels, and authorized representatives may submit copyright notices.</p>
          <p>PrimeTime DJ Pool may contact claimants to request additional information when appropriate to verify ownership or authorization.</p>
          <p>Supporting licensing agreements, ownership records, or authorization letters may help verification but are not required for every valid copyright notice.</p>
        </div>

        <div className="mx-auto mt-8 max-w-2xl rounded-3xl border border-prime-red/70 bg-black/70 p-7 text-center shadow-[0_0_40px_rgba(229,9,20,0.2)] sm:mt-10 sm:p-9">
          <h2 className="text-xl font-black uppercase tracking-[0.18em] text-white sm:text-2xl">Email Your Copyright Notice</h2>
          <a
            href="mailto:dmca@primetimedjpool.com?subject=Copyright%20Notice%20-%20PrimeTime%20DJ%20Pool"
            className="mt-5 inline-flex max-w-full items-center justify-center gap-3 break-all text-xl font-black text-prime-red transition-colors hover:text-red-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-prime-red/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:text-3xl"
          >
            <Mail aria-hidden="true" className="shrink-0" size={28} strokeWidth={2.1} />
            <span>dmca@primetimedjpool.com</span>
          </a>
          <p className="mt-5 text-sm text-white/65 sm:text-base">We may contact you for additional information or verification.</p>
        </div>
      </div>
    </section>
  );
}
